import * as THREE from 'three';
import { G, mesh } from './scene';
import { texSpark } from './vfx-tex';
/* ============================================================
   大招光束：層疊方塊（回到最原始的作法再加強）
   ============================================================

   使用者 2026-08-05：「光束幫我重做，我對現在的顯示方式不滿意，
   **用最原始的版本做加強**」。

   最原始那版（`world/player.ts` 裡的三層 BoxGeometry：白核 + 青光暈 +
   地面亮線）中間被換成過「交叉面片 + 能量流 shader」與「硬邊分層 shader」，
   兩版都被否掉。回到方塊有三個實際的好處：

   1. **它是真的立體**。交叉面片在鏡頭斜看時會露出「兩片紙」的破綻，
      方塊從任何角度都是一根有厚度的柱體 —— 那是「砲擊」而不是「貼圖」。
   2. **不吃 shader**。前一版整根都靠 fragment 算，而這個專案的瓶頸就在
      fragment（見 `platform/quality.ts`）。四個方塊是四個 draw call，零 shader 成本。
   3. **亮度好控制**。每一層就是一個 opacity 常數，要壓亮度直接改數字，
      不必回去推 shader 的合成式（前後調了四輪都在跟那個式子打架）。

   ## 「加強」加在哪裡

   原始版是三層固定寬度的方塊。這一版加了四件事：

   - **四層而不是三層**：核心 / 內層 / 外層 / 極淡的外暈。層數是「這根很厚」
     的唯一線索，三層在寬度拉大之後中間會空掉。
   - **地面亮線留著並加寬**：俯視鏡頭下光束本體與地面幾乎重疊，
     地上那一條是「它掃過哪一排」最清楚的指示。
   - **發射口的錐體**：光束不該是憑空出現的一段柱子，根部要有一個
     「從這裡噴出來」的收束。
   - **每幀的寬度抖動與亮度脈動**：固定寬度讀成靜態貼圖，會抖才像能量。

   顏色由呼叫端給（`world/player.ts` 目前是白核 + 藍）。 */

export type BeamRig = {
  group: THREE.Group;
  layers: any[];          // 由內而外
  line: any;              // 地面亮線
  cone: any;              // 發射口收束
  len: number;
};

function addMat(color: number, op: number) {
  return new THREE.MeshBasicMaterial({
    color: color, transparent: true, opacity: op,
    blending: THREE.AdditiveBlending, depthWrite: false
  });
}

/* 四層的相對半寬與不透明度。**這兩排數字就是光束的長相** ——
   要更粗改第一排、要更亮改第二排，不要動別的地方。
   不透明度刻意壓低，只有最內層那一絲會過泛光門檻（0.74，見 `render/postfx.ts`）
   —— 使用者三次回報「太亮」的解就在這裡。

   ⚠️ **地面亮線不能太寬太亮**：第一版給到 W[2]×1.6 寬、0.34 不透明度，
   結果整根光束在畫面上讀成「地上一條灰色的路面標線」，柱體本身完全被壓掉。
   它是輔助判讀用的，比重要遠低於柱體。 */
var W = [0.5, 1.5, 2.8, 4.2];
/* 核心 0.9 → 0.60：加粗之後白核的面積跟著放大，整根又回到「一條白帶」。
   粗的要是**藍色的本體**，白的那一絲只是中心線。 */
var OP = [0.60, 0.40, 0.22, 0.09];

/** 建一根光束。放進 `beamPivot`，往局部 +Z 打出去，起點在原點。 */
export function buildBeam(len: number, y: number, core: number, edge: number): BeamRig {
  var g = new THREE.Group();
  var layers: any[] = [];
  for (var i = 0; i < W.length; i++) {
    /* 由內而外顏色從核心色漸變到邊緣色。最外兩層純邊緣色 ——
       白色如果鋪到外層，整根就會讀成一條白帶（前一版的病）。 */
    var c = i === 0 ? core : (i === 1 ? mix(core, edge, 0.65) : edge);
    var m = mesh(G.box, addMat(c, OP[i]), [W[i], W[i], len], [0, y, len / 2]);
    m.renderOrder = 7 - i;              // 內層後畫，蓋在外層之上
    m.frustumCulled = false;
    g.add(m); layers.push(m);
  }
  /* 地面亮線：原始版就有，這一版加寬。它不是裝飾 ——
     33 公尺高的俯視鏡頭下，光束本體與地面在畫面上幾乎重疊，
     玩家判斷「掃到哪一排敵人」靠的是地上這一條。 */
  var line = mesh(G.box, addMat(edge, 0.16), [W[2] * 0.8, 0.05, len], [0, 0.09, len / 2]);
  line.renderOrder = 4; line.frustumCulled = false;
  g.add(line);
  /* 發射口：一個朝前的錐，讓光束有「從砲口噴出來」的根部。
     用 `texSpark` 的尖尾當貼圖不合適（那是平面），所以直接用錐體幾何。 */
  var cone = mesh(G.cone, addMat(core, 0.5), [W[3] * 1.2, 2.6, W[3] * 1.2], [0, y, 1.1], [Math.PI / 2, 0, 0]);
  cone.renderOrder = 8; cone.frustumCulled = false;
  g.add(cone);
  return { group: g, layers: layers, line: line, cone: cone, len: len };
}

function mix(a: number, b: number, t: number) {
  var ca = new THREE.Color(a), cb = new THREE.Color(b);
  return ca.lerp(cb, t).getHex();
}

/** 每幀更新。`fade` 0…1 是整體強度（含頭尾淡入淡出），
    `wMul` 是寬度倍率（聚焦模式會愈打愈粗）。 */
export function updateBeam3D(r: BeamRig, t: number, fade: number, wMul: number) {
  if (!r) return;
  /* 兩個不同頻率的抖動相乘：單一正弦會讀成規律的呼吸（假），
     相乘之後的不規則才像能量。振幅只有 8%。 */
  var jitter = 0.92 + 0.08 * Math.sin(t * 41) * Math.sin(t * 11.3);
  var w = fade * wMul * jitter;
  for (var i = 0; i < r.layers.length; i++) {
    var m = r.layers[i];
    m.scale.set(W[i] * w, W[i] * w, r.len);
    /* 外層比內層晚一點達到峰值：發射的那一拍先出現細的核心，
       外暈隨後撐開。差 0.12 秒，剛好是一個「轟」的長度。 */
    var lag = Math.min(1, fade * (1 + i * 0.35));
    m.material.opacity = OP[i] * lag * (i === 0 ? 1 : jitter);
  }
  r.line.scale.set(W[2] * 0.8 * w, 0.05, r.len);
  r.line.material.opacity = 0.16 * fade;
  var cs = W[3] * 1.2 * w * (0.9 + 0.2 * Math.sin(t * 27));
  r.cone.scale.set(cs, 2.6 * fade, cs);
  r.cone.material.opacity = 0.5 * fade * jitter;
}
